import { useRecoilValue } from "recoil";
import { Switch } from "../../ui-elements";
import { Typography } from "../Shadcn/Typography";
import { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent } from "../Shadcn/Tooltip";
import { checkboxState, CIFType, selAggLayerState, hexLoadingState } from "../../states";
import { METRIC_DATA, PARENT_METRIC_TYPES } from "../../constants";

interface CifTypeSwitchProps {
    layer: any
    cifType: CIFType[];
    isChecked: boolean;
    handleToggleChange: (event: any) => void;
    disabled: boolean;
    selMetric: {
        EH: any;
        CTV: any;
        PoI: any;
    };
    isPoiOverlapped: boolean;
    customTooltip: string;
    catchment?: any;
    choroplethLimitReached?: boolean
}

export const CifTypeSwitch: React.FC<CifTypeSwitchProps> = ({ layer, isChecked, handleToggleChange, disabled, isPoiOverlapped, customTooltip }) => {
    const selAggLayer = useRecoilValue(selAggLayerState);
    const isCheckedRegion = useRecoilValue(checkboxState);
    const hexLoading = useRecoilValue(hexLoadingState);
    const isAdjacencyLoading = layer.type === 'adjacency' && hexLoading.loading && isChecked;

    const getToggleKey = () => {
        const knownToggles = ['EH', 'CTV', 'PoI', 'MBP', 'spotlight', 'poimarker', 'adjacency', 'subregionBoundaries', 'catchments'];
        return knownToggles.includes(layer.type) ? layer.type : layer?.markertype?.toLowerCase() === 'poi' ? layer.type : (layer.layer_id || layer.type);
    };
    const toggleKey = getToggleKey();

    const getSwitchTitle = PARENT_METRIC_TYPES.includes(layer.type) && METRIC_DATA[layer.type as keyof typeof METRIC_DATA]?.key && layer.type !== METRIC_DATA[layer.type as keyof typeof METRIC_DATA]?.key ? METRIC_DATA[layer.type as keyof typeof METRIC_DATA]?.title : layer.title;
    const getTooltipText = customTooltip ? customTooltip : selAggLayer == null ? "Turn on Choropleth to enable this toggle." : layer?.type == "CTV" && isCheckedRegion?.catchments ? "Disabled due to Catchment" : `We do not have ${layer.type} at this level`;

    const showSwitchTooltip = disabled;

    return (
        <div className={`d-flex align-items-start justify-content-between p-0`}>
            <div className='w-full d-flex justify-content-between align-items-center mb-[var(--semantic-s)]'>
                <Typography
                    variant={layer.type === 'data-layers' ? 'b3' : 'b2'}
                    fontFamily="mono"
                    size="2xs"
                    weight="medium"
                    lineHeight="2xs"
                    letterSpacing="spaced"
                    className="gap-1 truncate uppercase text-[var(--sidebar-sidebar-text-secondary,#5A6574)]"
                >
                    <div className={disabled || layer?.status == "inprogress" ? 'opacity-50 ' : ''}>
                        {`${getSwitchTitle}${(layer.type === 'PoI' && isPoiOverlapped) ? '*' : ''}`}

                        {layer.status === 'inprogress' && (
                            <span
                                className="spinner-border spinner-border-sm ms-1"
                                role="status"
                                aria-label="Loading"
                                style={{ borderWidth: '0.1em', color: '#adb5bd', width: '0.75em', height: '0.75em', verticalAlign: 'middle' }}
                            />
                        )}

                        {layer.description && (
                            <TooltipProvider>
                                <TooltipRoot>
                                    <TooltipTrigger asChild>
                                        <span
                                            className="icon-gray-0 material-symbols-outlined fs-16 ms-1"
                                            style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                        >
                                            info
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-48 text-xs text-[var(--general-text-primary)] bg-[var(--general-molecules-bg-bg-white)] shadow-md">
                                        <span dangerouslySetInnerHTML={{ __html: layer.description?.replace(/\\n/g, '<br>') }} />
                                    </TooltipContent>
                                </TooltipRoot>
                            </TooltipProvider>
                        )}
                    </div>
                </Typography>
            </div>
            {isAdjacencyLoading && <div className="d-flex justify-content-end align-items-center col-2 p-1">
                {layer.hasOwnProperty('isselected') && (
                    isAdjacencyLoading ? (
                        <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-label="Loading adjacency"
                            style={{ borderWidth: '0.1em', color: '#adb5bd' }}
                        />
                    ) : (
                        <TooltipProvider>
                            <TooltipRoot open={showSwitchTooltip ? undefined : false}>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Switch
                                            isChecked={isChecked}
                                            toggleSwitch={handleToggleChange}
                                            name={toggleKey}
                                            disabled={disabled || layer?.status === "inprogress"}
                                        />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    {getTooltipText}
                                </TooltipContent>
                            </TooltipRoot>
                        </TooltipProvider>
                    )
                )}
            </div>}
        </div>
    )
};
